<template>
  <div class="skeleton-loader" :class="skeletonClass">
    <div v-if="type === 'text'" class="skeleton-text" :style="{ width: width }"></div>
    <div v-else-if="type === 'title'" class="skeleton-title" :style="{ width: width }"></div>
    <div v-else-if="type === 'avatar'" class="skeleton-avatar" :style="{ width: size, height: size }"></div>
    <div v-else-if="type === 'button'" class="skeleton-button" :style="{ width: width }"></div>
    <div v-else-if="type === 'card'" class="skeleton-card">
      <div class="skeleton-card-header">
        <div class="skeleton-avatar"></div>
        <div class="skeleton-card-text">
          <div class="skeleton-title sk-w-60"></div>
          <div class="skeleton-text sk-w-40"></div>
        </div>
      </div>
      <div class="skeleton-card-body">
        <div class="skeleton-text"></div>
        <div class="skeleton-text sk-w-80"></div>
        <div class="skeleton-text sk-w-60"></div>
      </div>
    </div>
    <div v-else-if="type === 'list-item'" class="skeleton-list-item">
      <div class="skeleton-avatar-small"></div>
      <div class="skeleton-list-content">
        <div class="skeleton-text sk-w-70"></div>
        <div class="skeleton-text skeleton-text-small sk-w-50"></div>
      </div>
      <div class="skeleton-button-small"></div>
    </div>
    <div v-else class="skeleton-box" :style="{ width: width, height: height }"></div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface Props {
  type?: 'text' | 'title' | 'avatar' | 'button' | 'card' | 'list-item' | 'box'
  width?: string
  height?: string
  size?: string
  animated?: boolean
  rounded?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  type: 'text',
  width: '100%',
  height: '20px',
  size: '40px',
  animated: true,
  rounded: false
})

const skeletonClass = computed(() => ({
  'skeleton-animated': props.animated,
  'skeleton-rounded': props.rounded
}))
</script>

<style scoped>
.skeleton-loader {
  --skeleton-bg: #e5e7eb;
  --skeleton-highlight: #f3f4f6;
}

/* Base skeleton styles */
.skeleton-text,
.skeleton-title,
.skeleton-avatar,
.skeleton-button,
.skeleton-box,
.skeleton-avatar-small,
.skeleton-button-small {
  background: var(--skeleton-bg);
  border-radius: 4px;
  position: relative;
  overflow: hidden;
}

.skeleton-text {
  height: 14px;
  margin-bottom: 8px;
}

.skeleton-text-small {
  height: 12px;
}

.skeleton-title {
  height: 24px;
  margin-bottom: 12px;
}

.skeleton-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  flex-shrink: 0;
}

.skeleton-avatar-small {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  flex-shrink: 0;
}

.skeleton-button {
  height: 40px;
  border-radius: 8px;
}

.skeleton-button-small {
  width: 80px;
  height: 32px;
  border-radius: 6px;
}

.skeleton-box {
  background: var(--skeleton-bg);
  border-radius: 4px;
}

/* Card skeleton */
.skeleton-card {
  background: white;
  border: 1px solid var(--skeleton-bg);
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 16px;
}

.skeleton-card-header {
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
}

.skeleton-card-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.skeleton-card-body .skeleton-text:last-child {
  margin-bottom: 0;
}

/* List item skeleton */
.skeleton-list-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 0;
  border-bottom: 1px solid var(--skeleton-bg);
}

.skeleton-list-content {
  flex: 1;
}

.skeleton-list-content .skeleton-text:last-child {
  margin-bottom: 0;
}

/* Animation */
.skeleton-animated .skeleton-text,
.skeleton-animated .skeleton-title,
.skeleton-animated .skeleton-avatar,
.skeleton-animated .skeleton-button,
.skeleton-animated .skeleton-box,
.skeleton-animated .skeleton-avatar-small,
.skeleton-animated .skeleton-button-small {
  background: linear-gradient(
    90deg,
    var(--skeleton-bg) 0%,
    var(--skeleton-highlight) 50%,
    var(--skeleton-bg) 100%
  );
  background-size: 200% 100%;
  animation: skeleton-shimmer 1.5s ease-in-out infinite;
}

@keyframes skeleton-shimmer {
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: 100% 0;
  }
}

/* Rounded variant */
.skeleton-rounded .skeleton-text,
.skeleton-rounded .skeleton-title,
.skeleton-rounded .skeleton-button,
.skeleton-rounded .skeleton-box {
  border-radius: 8px;
}

/* Width presets for skeleton elements */
.sk-w-40 { width: 40%; }
.sk-w-50 { width: 50%; }
.sk-w-60 { width: 60%; }
.sk-w-70 { width: 70%; }
.sk-w-80 { width: 80%; }

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .skeleton-loader {
    --skeleton-bg: #374151;
    --skeleton-highlight: #4b5563;
  }
  
  .skeleton-card {
    background: #1f2937;
    border-color: #374151;
  }
}
</style>
